import { toNumber } from "@/shared/utils";
import type { InventoryUnit, AggregatedInventoryRow, IndividualInventoryRow, WarehouseTableRow, UnitsSummaryResponse } from "../types";

export interface WarehouseSummary {
  totalUnits: number;
  inStockCount: number;
  assignedCount: number;
  writtenOffCount: number;
  disposedCount: number;
  totalValue: string;
  totalResidualValue: string;
  fullyDepreciatedCount: number;
  totalAccumulatedDepreciation: string;
}

export function calculateWarehouseSummary(units: InventoryUnit[]): WarehouseSummary {
  let totalUnits = 0;
  let inStockCount = 0;
  let assignedCount = 0;
  let writtenOffCount = 0;
  let disposedCount = 0;
  let totalValue = 0;
  let totalResidualValue = 0;
  let fullyDepreciatedCount = 0;

  for (const unit of units) {
    totalUnits++;

    if (unit.status === "in_stock") {
      inStockCount++;
    } else if (unit.status === "assigned") {
      assignedCount++;
    } else if (unit.status === "written_off") {
      writtenOffCount++;
    } else if (unit.status === "disposed") {
      disposedCount++;
    }

    if (unit.is_fully_depreciated) {
      fullyDepreciatedCount++;
    }

    totalValue += toNumber(unit.unit_cost);
    totalResidualValue += toNumber(unit.residual_value);
  }

  return {
    totalUnits,
    inStockCount,
    assignedCount,
    writtenOffCount,
    disposedCount,
    totalValue: totalValue.toFixed(2),
    totalResidualValue: totalResidualValue.toFixed(2),
    fullyDepreciatedCount,
    totalAccumulatedDepreciation: "0.00",
  };
}

export function mapSummaryResponse(response: UnitsSummaryResponse): WarehouseSummary {
  return {
    totalUnits: response.total_units,
    inStockCount: response.in_stock_count,
    assignedCount: response.assigned_count,
    writtenOffCount: response.written_off_count,
    disposedCount: response.disposed_count,
    totalValue: response.total_value,
    totalResidualValue: response.total_residual_value,
    fullyDepreciatedCount: response.fully_depreciated_count,
    totalAccumulatedDepreciation: response.total_accumulated_depreciation,
  };
}

export function aggregateInventoryUnits(units: InventoryUnit[]): WarehouseTableRow[] {
  const inventoryUnits: InventoryUnit[] = [];
  const otherUnits: InventoryUnit[] = [];

  for (const unit of units) {
    if (unit.asset_type === "inventory") {
      inventoryUnits.push(unit);
    } else {
      otherUnits.push(unit);
    }
  }

  const batchMap = new Map<number, InventoryUnit[]>();
  for (const unit of inventoryUnits) {
    const batch = batchMap.get(unit.inventory_batch);
    if (batch) {
      batch.push(unit);
    } else {
      batchMap.set(unit.inventory_batch, [unit]);
    }
  }

  const aggregated: AggregatedInventoryRow[] = [];
  for (const [batchId, batchUnits] of batchMap) {
    const first = batchUnits[0];
    let inStockCount = 0;
    let assignedCount = 0;
    let writtenOffCount = 0;

    for (const u of batchUnits) {
      if (u.status === "in_stock") inStockCount++;
      else if (u.status === "assigned") assignedCount++;
      else if (u.status === "written_off") writtenOffCount++;
    }

    const totalCount = batchUnits.length;
    const unitCostNum = toNumber(first.unit_cost);

    aggregated.push({
      kind: "aggregated",
      inventoryBatchId: batchId,
      itemName: first.item_name,
      assetType: "inventory",
      inventorySubtype: first.inventory_subtype,
      vendor: first.vendor ?? null,
      unitCost: first.unit_cost,
      totalCost: (unitCostNum * totalCount).toFixed(2),
      inStockCount,
      assignedCount,
      writtenOffCount,
      totalCount,
      units: batchUnits,
    });
  }

  const individual: IndividualInventoryRow[] = otherUnits.map((unit) => ({
    kind: "individual",
    unit,
  }));

  return [...aggregated, ...individual];
}


